import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import IncomingRequests from '../components/IncomingRequests';
import OwnerEarningsTab from '../components/OwnerEarningsTab';
import AdminAccountManagement from '../components/AdminAccountManagement';
import TwoFactorSettings from '../components/TwoFactorSettings';
import ProviderPerformanceAnalytics from '../components/ProviderPerformanceAnalytics';
import AIPerformanceMonitor from '../components/AIPerformanceMonitor';
import IntegrationStatus from '../components/IntegrationStatus';
import OwnerRevenueHub from '../components/OwnerRevenueHub';
import BackupDashboard from '../components/BackupDashboard';
import PayoutApprovalDashboard from '../components/PayoutApprovalDashboard';
import RetentionBonusDashboard from '../components/RetentionBonusDashboard';
import ScraperDashboard from '../components/ScraperDashboard';
import AutoPartsOrderApproval from '../components/AutoPartsOrderApproval';
import RefundLogsDashboard from '../components/RefundLogsDashboard';
import LeadsManagement from '../components/LeadsManagement';
import SiteStatsEditor from '../components/SiteStatsEditor';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import {
  Eye, Users, MessageSquare, Briefcase, Clock, MapPin, DollarSign, 
  Check, X, Pause, Play, Send, RefreshCw, Shield, Truck, Wrench,
  AlertCircle, Timer, Navigation, Star, Phone, CreditCard, Building2,
  FileText, Settings, Plus, Edit, Trash2, Mail, BarChart3, Palette,
  Car, Bell, IdCard, CheckCircle, XCircle, Loader2, UserX, UserPlus,
  Power, PowerOff, AlertTriangle, Zap, Activity, TrendingUp, Database, Wallet, Gift, Key, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get session ID from URL if present (for notification deep links)
  const sessionIdFromUrl = searchParams.get('session');
  const tabFromUrl = searchParams.get('tab');
  
  const [loading, setLoading] = useState(true);
  const [liveData, setLiveData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Admin data states
  const [pendingProviders, setPendingProviders] = useState([]);
  const [applications, setApplications] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Payments & Documents states
  const [allPayments, setAllPayments] = useState({ customers: [], sellers: [], employees: [], summary: {} });
  const [allDocuments, setAllDocuments] = useState([]);
  const [directDeposits, setDirectDeposits] = useState([]);
  
  // Live Activity & All Requests states
  const [liveActivity, setLiveActivity] = useState({ all_users: [], summary: {} });
  const [allRequests, setAllRequests] = useState({ all_requests: [], summary: {} });
  const [pendingAccounts, setPendingAccounts] = useState({ pending_users: [], pending_applications: [], pending_documents: [], summary: {} });
  
  // Service form
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    service_type: '',
    name: '',
    price: '',
    category: 'roadside',
    description: ''
  });

  const fetchLiveData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/owner/live-jobs`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setLiveData(response.data);
    } catch (error) {
      console.error('Failed to fetch live data:', error);
    }
  }, [getAuthHeaders]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/owner/all-conversations`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, [getAuthHeaders]);

  const fetchAdminData = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      
      const [pendingRes, appsRes, usersRes, servicesRes, notifsRes] = await Promise.all([
        axios.get(`${API}/admin/pending-providers`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/applications`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/services/catalog`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/notifications`, { headers, withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setPendingProviders(pendingRes.data || []);
      setApplications(appsRes.data || []);
      setAllUsers(usersRes.data || []);
      setServices(servicesRes.data || []);
      setNotifications(notifsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
  }, [getAuthHeaders]);

  const fetchPaymentsAndDocs = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      
      const [paymentsRes, docsRes] = await Promise.all([
        axios.get(`${API}/owner/all-payments`, { headers, withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/owner/all-documents`, { headers, withCredentials: true }).catch(() => ({ data: { documents: [], direct_deposits: [] } }))
      ]);
      
      setAllPayments(paymentsRes.data || { customers: [], sellers: [], employees: [], summary: {} });
      setAllDocuments(docsRes.data?.all_documents || docsRes.data?.documents || []);
      setDirectDeposits(docsRes.data?.direct_deposits || []);
    } catch (error) {
      console.error('Failed to fetch payments/docs:', error);
    }
  }, [getAuthHeaders]);

  const fetchLiveActivity = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/live-activity`, { headers, withCredentials: true });
      setLiveActivity(response.data || { all_users: [], summary: {} });
    } catch (error) {
      console.error('Failed to fetch live activity:', error);
    }
  }, [getAuthHeaders]);

  const fetchAllRequests = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/all-requests`, { headers, withCredentials: true });
      setAllRequests(response.data || { all_requests: [], summary: {} });
    } catch (error) {
      console.error('Failed to fetch all requests:', error);
    }
  }, [getAuthHeaders]);

  const fetchPendingAccounts = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/pending-accounts`, { headers, withCredentials: true });
      setPendingAccounts(response.data || { pending_users: [], pending_applications: [], pending_documents: [], summary: {} });
    } catch (error) {
      console.error('Failed to fetch pending accounts:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!user?.is_owner) {
      toast.error('Owner access required');
      navigate('/');
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchLiveData(), 
        fetchConversations(), 
        fetchAdminData(), 
        fetchPaymentsAndDocs(),
        fetchLiveActivity(),
        fetchAllRequests(),
        fetchPendingAccounts()
      ]);
      setLoading(false);
    };
    
    loadData();
    
    // Refresh every 10 seconds
    const interval = setInterval(() => {
      fetchLiveData();
      fetchLiveActivity();
      fetchAllRequests();
    }, 10000);
    return () => clearInterval(interval);
  }, [user, navigate, fetchLiveData, fetchConversations, fetchAdminData, fetchPaymentsAndDocs, fetchLiveActivity, fetchAllRequests, fetchPendingAccounts]);

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      await axios.post(`${API}/messages/${selectedConversation.conversation_id}`, {
        content: newMessage
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setNewMessage('');
      await loadMessages(selectedConversation.conversation_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const pauseConversation = async (conversationId, reason = 'Paused for review') => {
    try {
      await axios.put(`${API}/messages/conversation/${conversationId}/pause`, { reason }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Conversation paused');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to pause conversation');
    }
  };

  const resumeConversation = async (conversationId) => {
    try {
      await axios.put(`${API}/messages/conversation/${conversationId}/resume`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Conversation resumed');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to resume conversation');
    }
  };

  const aiMatchProviders = async (requestId) => {
    try {
      const response = await axios.post(`${API}/jobs/ai-match/${requestId}`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Found ${response.data.total_found} matching providers`);
      fetchLiveData();
      return response.data;
    } catch (error) {
      toast.error('Failed to match providers');
    }
  };

  const approveAssignment = async (requestId, providerId, action) => {
    try {
      await axios.put(`${API}/admin/jobs/${requestId}/approve-assignment`, {
        action,
        provider_id: providerId
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(action === 'approve' ? 'Assignment approved!' : 'Assignment denied');
      fetchLiveData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process assignment');
    }
  };

  const holdOffer = async (offerId, reason) => {
    try {
      await axios.put(`${API}/jobs/offers/${offerId}/hold`, { reason }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Offer placed on hold');
      fetchLiveData();
    } catch (error) {
      toast.error('Failed to hold offer');
    }
  };

  const releaseOffer = async (offerId) => {
    try {
      await axios.put(`${API}/jobs/offers/${offerId}/release`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Offer released');
      fetchLiveData();
    } catch (error) {
      toast.error('Failed to release offer');
    }
  };

  // Admin functions
  const handleProviderAction = async (userId, action, reason = '') => {
    try {
      await axios.put(
        `${API}/admin/providers/${userId}/approve`,
        { action, reason },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Provider ${action}d successfully`);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} provider`);
    }
  };

  const handleToggleAdmin = async (userId, makeAdmin) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/admin`,
        { is_admin: makeAdmin },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Admin status updated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/services`,
        { ...serviceForm, price: parseFloat(serviceForm.price) },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(editingService ? 'Service updated' : 'Service created');
      setShowServiceForm(false);
      setEditingService(null);
      setServiceForm({ service_type: '', name: '', price: '', category: 'roadside', description: '' });
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceType) => {
    if (!window.confirm('Are you sure you want to deactivate this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${serviceType}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Service deactivated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const editService = (service) => {
    setEditingService(service);
    setServiceForm({
      service_type: service.service_type,
      name: service.name,
      price: service.price.toString(),
      category: service.category,
      description: service.description || ''
    });
    setShowServiceForm(true);
  };

  // Document verification
  const handleVerifyDocument = async (documentId, action, reason = '') => {
    try {
      await axios.put(`${API}/owner/documents/${documentId}/verify`, { action, reason }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Document ${action}d`);
      fetchPaymentsAndDocs();
    } catch (error) {
      toast.error('Failed to verify document');
    }
  };

  // Process payment (mock)
  const handleProcessPayment = async (userId, paymentType, amount) => {
    try {
      await axios.post(`${API}/owner/process-payment`, { user_id: userId, payment_type: paymentType, amount }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Payment processed (MOCKED)');
      fetchPaymentsAndDocs();
    } catch (error) {
      toast.error('Failed to process payment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const pendingApps = applications.filter(a => a.status === 'pending');
  const pendingDocs = allDocuments.filter(d => d.status === 'pending');

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-20 sm:pb-0" data-testid="owner-dashboard">
      <Header />
      
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Owner Header */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#191919] flex items-center gap-2">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-[#FF3008]" />
              <span className="hidden sm:inline">Owner Command Center</span>
              <span className="sm:hidden">Command Center</span>
            </h1>
            <p className="text-sm sm:text-base text-[#767676]">Full access to all platform operations</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/analytics">
              <Button variant="outline" size="sm" className="h-8 px-2 sm:px-3">
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Analytics</span>
              </Button>
            </Link>
            <Link to="/owner/price-alerts">
              <Button variant="outline" size="sm" className="h-8 px-2 sm:px-3 border-yellow-300 hover:bg-yellow-50">
                <Bell className="w-4 h-4 sm:mr-2 text-yellow-600" />
                <span className="hidden sm:inline">Price Alerts</span>
              </Button>
            </Link>
            <Link to="/owner/ai-marketplace">
              <Button variant="outline" size="sm" className="h-8 px-2 sm:px-3 border-[#FF3008]/30 hover:bg-[#FFF0ED]">
                <Zap className="w-4 h-4 sm:mr-2 text-[#FF3008]" />
                <span className="hidden sm:inline">AI Marketplace</span>
              </Button>
            </Link>
            <Link to="/admin/design">
              <Button variant="outline" size="sm" className="h-8 px-2 sm:px-3">
                <Palette className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Design</span>
              </Button>
            </Link>
            <Button onClick={() => { fetchLiveData(); fetchAdminData(); fetchPaymentsAndDocs(); fetchLiveActivity(); fetchAllRequests(); fetchPendingAccounts(); }} variant="outline" size="sm" className="h-8 px-2 sm:px-3">
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards - Mobile Optimized */}
        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Card className="border-[#FF3008]/20 bg-[#FFF0ED]">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-[#FF3008] font-medium">Jobs</p>
                  <p className="text-lg sm:text-xl font-bold text-[#191919]">{liveData?.total_active || 0}</p>
                </div>
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF3008]/40 hidden sm:block" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-orange-600 font-medium">Offers</p>
                  <p className="text-lg sm:text-xl font-bold text-orange-900">{liveData?.total_pending_offers || 0}</p>
                </div>
                <Timer className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-green-600 font-medium">Live</p>
                  <p className="text-lg sm:text-xl font-bold text-green-900">{liveActivity?.total_active || 0}</p>
                </div>
                <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-blue-600 font-medium">Users</p>
                  <p className="text-lg sm:text-xl font-bold text-blue-900">{allUsers.length}</p>
                </div>
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-cyan-200 bg-cyan-50 hidden lg:block">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-cyan-600 font-medium">Requests</p>
                  <p className="text-lg sm:text-xl font-bold text-cyan-900">{allRequests?.total || 0}</p>
                </div>
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-yellow-200 bg-yellow-50 hidden lg:block">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-yellow-600 font-medium">Approvals</p>
                  <p className="text-lg sm:text-xl font-bold text-yellow-900">{pendingProviders.length}</p>
                </div>
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50 hidden lg:block">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-red-600 font-medium">Docs</p>
                  <p className="text-lg sm:text-xl font-bold text-red-900">{pendingDocs.length}</p>
                </div>
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-indigo-200 bg-indigo-50 hidden lg:block">
            <CardContent className="p-2 sm:pt-3 sm:pb-3">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-1">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-indigo-600 font-medium">Providers</p>
                  <p className="text-lg sm:text-xl font-bold text-indigo-900">{liveData?.online_providers?.length || 0}</p>
                </div>
                <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400 hidden sm:block" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue={tabFromUrl || "accept-jobs"} className="space-y-3 sm:space-y-4">
          {/* Mobile-Optimized Scrollable Tab List */}
          <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
            <TabsList className="bg-white inline-flex sm:flex-wrap h-auto gap-1 p-1 min-w-max sm:min-w-0">
            <TabsTrigger value="accept-jobs" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Accept</span>
            </TabsTrigger>
            <TabsTrigger value="live-activity" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Live</span>
              {liveActivity?.total_active > 0 && (
                <span className="bg-green-500 text-white text-xs px-1 py-0.5 rounded-full ml-0.5">{liveActivity.total_active}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="all-requests" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Briefcase className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Requests</span>
              {allRequests?.summary?.total_pending > 0 && (
                <span className="bg-cyan-500 text-white text-xs px-1 py-0.5 rounded-full ml-0.5">{allRequests.summary.total_pending}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Jobs</span>
            </TabsTrigger>
            <TabsTrigger value="offers" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Timer className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Offers</span>
            </TabsTrigger>
            <TabsTrigger value="providers" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Approve</span>
              {pendingProviders.length > 0 && (
                <span className="bg-orange-500 text-white text-xs px-1 py-0.5 rounded-full ml-0.5">{pendingProviders.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Pay</span>
            </TabsTrigger>
            <TabsTrigger value="earnings" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Earn</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <IdCard className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Docs</span>
              {pendingDocs.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-1 py-0.5 rounded-full ml-0.5">{pendingDocs.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Svc</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Msg</span>
            </TabsTrigger>
            <TabsTrigger value="ai-monitor" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">AI</span>
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Log</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">2FA</span>
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Perf</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">APIs</span>
            </TabsTrigger>
            <TabsTrigger value="revenue" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Revenue</span>
            </TabsTrigger>
            <TabsTrigger value="payouts" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Wallet className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Payouts</span>
            </TabsTrigger>
            <TabsTrigger value="bonuses" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Gift className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Bonuses</span>
            </TabsTrigger>
            <TabsTrigger value="scraper" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Scraper</span>
            </TabsTrigger>
            <TabsTrigger value="auto-parts" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Wrench className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Parts Orders</span>
            </TabsTrigger>
            <TabsTrigger value="refunds" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Refunds</span>
            </TabsTrigger>
            <TabsTrigger value="backups" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Database className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Backup</span>
            </TabsTrigger>
            <TabsTrigger value="leads" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Leads</span>
            </TabsTrigger>
            <TabsTrigger value="site-stats" className="flex items-center gap-1 text-xs px-2 py-1.5 sm:px-3 sm:py-2 sm:text-sm">
              <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Site Stats</span>
            </TabsTrigger>
          </TabsList>
          </div>

          {/* Accept Jobs Tab */}
          <TabsContent value="accept-jobs">
            <div className="space-y-6">
              <IncomingRequests />
              
              {/* Admin Job Assignment */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    Assign Jobs to Providers
                  </CardTitle>
                  <CardDescription>
                    As owner, you can accept jobs yourself or assign them to specific providers from the All Requests tab
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </TabsContent>

          {/* Live Activity Tab */}
          <TabsContent value="live-activity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-green-600" />
                  Live User Activity
                  <Badge className="ml-2 bg-green-100 text-green-700">Auto-refreshes every 10s</Badge>
                </CardTitle>
                <CardDescription>Real-time view of all active users and their current pages</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Activity Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{liveActivity?.summary?.total_customers || 0}</p>
                    <p className="text-xs text-blue-700">Customers Online</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{liveActivity?.summary?.total_mechanics || 0}</p>
                    <p className="text-xs text-green-700">Mechanics Online</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{liveActivity?.summary?.total_roadside || 0}</p>
                    <p className="text-xs text-orange-700">Roadside Techs Online</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">{liveActivity?.summary?.total_admins || 0}</p>
                    <p className="text-xs text-purple-700">Admins Online</p>
                  </div>
                </div>

                {/* Active Users List */}
                {liveActivity?.all_users?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No active users right now</p>
                ) : (
                  <div className="space-y-3">
                    {liveActivity?.all_users?.map((activity, idx) => (
                      <div 
                        key={activity.user_id || activity.email || idx}
                        className={`p-4 rounded-lg border flex items-center justify-between ${
                          activity.user_type === 'owner' ? 'bg-purple-50 border-purple-200' :
                          activity.is_admin ? 'bg-indigo-50 border-indigo-200' :
                          activity.user_type === 'mechanic' ? 'bg-blue-50 border-blue-200' :
                          activity.user_type === 'roadside_tech' ? 'bg-green-50 border-green-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              activity.user_type === 'owner' ? 'bg-purple-100' :
                              activity.is_admin ? 'bg-indigo-100' :
                              activity.user_type === 'mechanic' ? 'bg-blue-100' :
                              activity.user_type === 'roadside_tech' ? 'bg-green-100' :
                              'bg-gray-100'
                            }`}>
                              {activity.user_type === 'owner' ? <Shield className="w-5 h-5 text-purple-600" /> :
                               activity.is_admin ? <Shield className="w-5 h-5 text-indigo-600" /> :
                               activity.user_type === 'mechanic' ? <Wrench className="w-5 h-5 text-blue-600" /> :
                               activity.user_type === 'roadside_tech' ? <Truck className="w-5 h-5 text-green-600" /> :
                               <Users className="w-5 h-5 text-gray-600" />}
                            </div>
                            <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{activity.user_name}</p>
                            <p className="text-xs text-gray-500">{activity.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            activity.current_page?.includes('owner') || activity.current_page?.includes('admin') ? 'bg-purple-100 text-purple-700' :
                            activity.current_page?.includes('mechanic') ? 'bg-blue-100 text-blue-700' :
                            activity.current_page?.includes('customer') ? 'bg-green-100 text-green-700' :
                            activity.current_page?.includes('marketplace') ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {activity.page_title || activity.current_page}
                          </Badge>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(activity.last_active).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Requests Tab */}
          <TabsContent value="all-requests">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                  All Service Requests
                  <Badge className="ml-2 bg-cyan-100 text-cyan-700">All Customers</Badge>
                </CardTitle>
                <CardDescription>View all pending and active service requests from all customers</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Request Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  <div className="p-3 bg-yellow-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-600">{allRequests?.summary?.total_pending || 0}</p>
                    <p className="text-xs text-yellow-700">Pending</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{allRequests?.summary?.total_accepted || 0}</p>
                    <p className="text-xs text-blue-700">Accepted</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{allRequests?.summary?.total_in_progress || 0}</p>
                    <p className="text-xs text-orange-700">In Progress</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{allRequests?.summary?.total_completed || 0}</p>
                    <p className="text-xs text-green-700">Completed</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{allRequests?.summary?.total_cancelled || 0}</p>
                    <p className="text-xs text-red-700">Cancelled</p>
                  </div>
                </div>

                {/* Requests List */}
                {allRequests?.all_requests?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No service requests found</p>
                ) : (
                  <div className="space-y-4">
                    {allRequests?.all_requests?.slice(0, 50).map((req) => (
                      <div 
                        key={req.request_id}
                        className={`p-4 rounded-lg border ${
                          req.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                          req.status === 'accepted' ? 'bg-blue-50 border-blue-200' :
                          req.status === 'in_progress' ? 'bg-orange-50 border-orange-200' :
                          req.status === 'completed' ? 'bg-green-50 border-green-200' :
                          'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={
                                req.status === 'pending' ? 'bg-yellow-600' :
                                req.status === 'accepted' ? 'bg-blue-600' :
                                req.status === 'in_progress' ? 'bg-orange-600' :
                                req.status === 'completed' ? 'bg-green-600' :
                                'bg-red-600'
                              }>
                                {req.status}
                              </Badge>
                              {req.owner_discount_applied && (
                                <Badge className="bg-purple-100 text-purple-700">OWNER - FREE</Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-lg">{req.service_name || req.service_type}</h3>
                            
                            {/* Customer Details */}
                            <div className="flex items-center gap-2 mt-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Customer: {req.customer_details?.name || req.customer_name || 'Unknown'}
                              </span>
                              {req.customer_details?.email && (
                                <span className="text-xs text-gray-400">({req.customer_details.email})</span>
                              )}
                            </div>
                            
                            {/* Provider Details */}
                            {req.provider_details && (
                              <div className="flex items-center gap-2 mt-1">
                                <Wrench className="w-4 h-4 text-blue-400" />
                                <span className="text-sm text-blue-600">
                                  Assigned: {req.provider_details.name}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                  <span className="text-xs text-gray-500">{req.provider_details.rating || 5.0}</span>
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                              <span className="flex items-center gap-1 text-gray-600">
                                <Car className="w-4 h-4" />
                                {req.vehicle_year} {req.vehicle_make} {req.vehicle_model}
                              </span>
                              <span className="flex items-center gap-1 text-green-600 font-semibold">
                                <DollarSign className="w-4 h-4" />
                                ${req.total_price || 0}
                              </span>
                              <span className="flex items-center gap-1 text-gray-400">
                                <Clock className="w-4 h-4" />
                                {new Date(req.created_at).toLocaleString()}
                              </span>
                            </div>
                            
                            {req.location?.address && (
                              <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
                                <MapPin className="w-4 h-4" />
                                {req.location.address}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            {req.status === 'pending' && (
                              <Button size="sm" variant="outline" onClick={() => aiMatchProviders(req.request_id)}>
                                AI Match
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Live Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Live Service Requests
                </CardTitle>
                <CardDescription>Monitor and manage all active jobs</CardDescription>
              </CardHeader>
              <CardContent>
                {liveData?.active_services?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No active service requests</p>
                ) : (
                  <div className="space-y-4">
                    {liveData?.active_services?.map((service) => (
                      <div key={service.request_id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant={service.status === 'pending' ? 'secondary' : service.status === 'accepted' ? 'default' : 'outline'}>
                                {service.status}
                              </Badge>
                              {service.owner_discount && (
                                <Badge variant="outline" className="bg-purple-100 text-purple-700">OWNER - FREE</Badge>
                              )}
                              {service.requires_admin_assignment_approval && (
                                <Badge variant="outline" className="bg-orange-100 text-orange-700">Needs Assignment Approval</Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-lg">{service.service_name}</h3>
                            <p className="text-sm text-gray-600">Customer: {service.customer_name}</p>
                            <p className="text-sm text-gray-500">
                              {service.vehicle_year} {service.vehicle_make} {service.vehicle_model}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                              <span className="flex items-center gap-1 text-gray-600">
                                <MapPin className="w-4 h-4" />
                                {service.location?.address || 'Unknown location'}
                              </span>
                              <span className="flex items-center gap-1 text-green-600 font-semibold">
                                <DollarSign className="w-4 h-4" />
                                ${service.total_price}
                              </span>
                            </div>
                            {service.mechanic_name && (
                              <p className="text-sm mt-2 text-blue-600">
                                Assigned to: {service.mechanic_name}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => aiMatchProviders(service.request_id)}
                            >
                              AI Match Providers
                            </Button>
                            
                            {service.ai_matched_providers?.length > 0 && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button size="sm" onClick={() => setSelectedJob(service)}>
                                    Approve Assignment
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                  <DialogHeader>
                                    <DialogTitle>Approve Provider Assignment</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 max-h-96 overflow-y-auto">
                                    {service.ai_matched_providers.map((provider, idx) => (
                                      <div key={provider.user_id} className="p-4 border rounded-lg flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                            provider.user_type === 'mechanic' ? 'bg-blue-100' : 'bg-green-100'
                                          }`}>
                                            {provider.user_type === 'mechanic' ? 
                                              <Wrench className="w-5 h-5 text-blue-600" /> : 
                                              <Truck className="w-5 h-5 text-green-600" />
                                            }
                                          </div>
                                          <div>
                                            <p className="font-medium">{provider.name}</p>
                                            <p className="text-sm text-gray-500">
                                              {provider.distance_miles} miles away - Score: {provider.score}
                                            </p>
                                            <div className="flex items-center gap-1 text-yellow-600">
                                              <Star className="w-3 h-3 fill-current" />
                                              <span className="text-sm">{provider.rating}</span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={() => approveAssignment(service.request_id, provider.user_id, 'approve')}
                                          >
                                            <Check className="w-4 h-4 mr-1" />
                                            Approve
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => approveAssignment(service.request_id, provider.user_id, 'deny')}
                                          >
                                            <X className="w-4 h-4 mr-1" />
                                            Deny
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Offers Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="w-5 h-5" />
                  Pending Job Offers (5-min Timer)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {liveData?.pending_offers?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No pending offers</p>
                ) : (
                  <div className="space-y-4">
                    {liveData?.pending_offers?.map((offer) => (
                      <div key={offer.offer_id} className={`p-4 border rounded-lg ${offer.on_hold ? 'bg-yellow-50 border-yellow-300' : ''}`}>
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge>{offer.service_name}</Badge>
                              {offer.on_hold && <Badge variant="outline" className="bg-yellow-100">ON HOLD</Badge>}
                            </div>
                            <p className="font-semibold">Offered to: {offer.provider_name}</p>
                            <p className="text-sm text-gray-600">Customer: {offer.customer_name}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4 text-green-600" />
                                ${offer.total_pay}
                              </span>
                              <span className="flex items-center gap-1">
                                <Navigation className="w-4 h-4 text-blue-600" />
                                {offer.estimated_miles} miles
                              </span>
                              <span className="flex items-center gap-1 text-orange-600">
                                <Clock className="w-4 h-4" />
                                Expires: {new Date(offer.expires_at).toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            {offer.on_hold ? (
                              <Button size="sm" onClick={() => releaseOffer(offer.offer_id)}>
                                <Play className="w-4 h-4 mr-1" />
                                Release
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => holdOffer(offer.offer_id, 'Admin review')}>
                                <Pause className="w-4 h-4 mr-1" />
                                Hold
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Provider Approvals Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-600" />
                  Pending Provider Registrations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingProviders.length === 0 ? (
                  <div className="text-center py-8">
                    <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-500">No pending provider registrations</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingProviders.map((provider) => (
                      <div 
                        key={provider.user_id}
                        className="p-4 sm:p-6 rounded-lg border-2 border-orange-200 bg-orange-50"
                      >
                        <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              provider.user_type === 'mechanic' ? 'bg-blue-100' : 
                              provider.user_type === 'admin' ? 'bg-purple-100' : 'bg-green-100'
                            }`}>
                              {provider.user_type === 'mechanic' ? (
                                <Wrench className="w-6 h-6 text-blue-600" />
                              ) : provider.user_type === 'admin' ? (
                                <Shield className="w-6 h-6 text-purple-600" />
                              ) : (
                                <Truck className="w-6 h-6 text-green-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                              <p className="text-sm text-gray-500 capitalize">
                                {provider.user_type === 'mechanic' ? 'Mobile Mechanic' : 
                                 provider.user_type === 'roadside_tech' ? 'Roadside Technician' : 
                                 provider.user_type === 'admin' ? 'Administrator' : provider.user_type}
                              </p>
                            </div>
                          </div>
                          <Badge className={provider.user_type === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}>
                            {provider.user_type === 'admin' ? 'ADMIN PENDING' : 'PENDING APPROVAL'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="text-sm truncate">{provider.email}</span>
                          </div>
                          {provider.phone && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4" />
                              <span className="text-sm">{provider.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">{new Date(provider.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {provider.skills?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Skills:</p>
                            <div className="flex flex-wrap gap-2">
                              {provider.skills.map((skill) => (
                                <span key={skill} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {provider.certifications?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Certifications:</p>
                            <div className="flex flex-wrap gap-2">
                              {provider.certifications.map((cert, idx) => (
                                <span key={cert || idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">{cert}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3 pt-4 border-t flex-wrap">
                          <Button
                            onClick={() => handleProviderAction(provider.user_id, 'approve')}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="destructive" className="flex-1">
                                <X className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Reject Provider Application</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={(e) => {
                                e.preventDefault();
                                const reason = e.target.reason.value;
                                handleProviderAction(provider.user_id, 'reject', reason);
                              }}>
                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="reason">Rejection Reason (Optional)</Label>
                                    <Textarea id="reason" name="reason" placeholder="Provide a reason for rejection..." className="mt-1" />
                                  </div>
                                  <Button type="submit" variant="destructive" className="w-full">Confirm Rejection</Button>
                                </div>
                              </form>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="space-y-6">
              {/* Payment Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">${allPayments.summary?.total_revenue?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Customer Payments</p>
                    <p className="text-2xl font-bold text-blue-600">{allPayments.customers?.length || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Seller Payouts Due</p>
                    <p className="text-2xl font-bold text-purple-600">${allPayments.summary?.seller_payouts_due?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Employee Payouts Due</p>
                    <p className="text-2xl font-bold text-orange-600">${allPayments.summary?.employee_payouts_due?.toFixed(2) || '0.00'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Customer Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    Customer Payment History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allPayments.customers?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No customer payments yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2">Customer</th>
                            <th className="text-left py-2 px-2">Service</th>
                            <th className="text-left py-2 px-2">Amount</th>
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allPayments.customers?.slice(0, 20).map((payment, idx) => (
                            <tr key={payment.payment_id || payment._id || idx} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-2">{payment.customer_name}</td>
                              <td className="py-2 px-2">{payment.service_name}</td>
                              <td className="py-2 px-2 text-green-600 font-medium">${payment.amount?.toFixed(2)}</td>
                              <td className="py-2 px-2 text-gray-500">{new Date(payment.created_at).toLocaleDateString()}</td>
                              <td className="py-2 px-2">
                                <Badge className={payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                  {payment.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seller Wallets */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    Seller Wallet Balances
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allPayments.sellers?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No seller wallets</p>
                  ) : (
                    <div className="space-y-3">
                      {allPayments.sellers?.map((seller, idx) => (
                        <div key={seller.user_id || seller.email || idx} className="p-3 border rounded-lg flex items-center justify-between">
                          <div>
                            <p className="font-medium">{seller.name}</p>
                            <p className="text-sm text-gray-500">{seller.email}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Balance</p>
                              <p className="text-lg font-bold text-green-600">${seller.wallet_balance?.toFixed(2)}</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handleProcessPayment(seller.user_id, 'seller_payout', seller.wallet_balance)}>
                              Process Payout
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Employee Direct Deposits */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-orange-600" />
                    Employee Direct Deposit Accounts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {directDeposits?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No direct deposit accounts</p>
                  ) : (
                    <div className="space-y-3">
                      {directDeposits?.map((deposit, idx) => (
                        <div key={deposit.account_id || deposit._id || idx} className="p-3 border rounded-lg flex items-center justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-8 h-8 text-gray-400" />
                            <div>
                              <p className="font-medium">{deposit.account_holder_name}</p>
                              <p className="text-sm text-gray-500">{deposit.bank_name} - {deposit.account_type} ****{deposit.account_number_last_four}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={deposit.status === 'active' ? 'bg-green-100 text-green-700' : deposit.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                              {deposit.status}
                            </Badge>
                            {deposit.status === 'pending' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleProcessPayment(deposit.user_id, 'verify_deposit', 0)}>
                                  <Check className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Owner Earnings Tab */}
          <TabsContent value="earnings">
            <OwnerEarningsTab />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IdCard className="w-5 h-5" />
                  Employee Documents Verification
                </CardTitle>
                <CardDescription>Review and verify employee ID/license uploads</CardDescription>
              </CardHeader>
              <CardContent>
                {allDocuments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No documents uploaded yet</p>
                ) : (
                  <div className="space-y-4">
                    {allDocuments.map((doc) => (
                      <div 
                        key={doc.document_id}
                        className={`p-4 rounded-lg border-2 ${
                          doc.status === 'verified' ? 'bg-green-50 border-green-200' :
                          doc.status === 'rejected' ? 'bg-red-50 border-red-200' :
                          'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-start justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              doc.document_type === 'drivers_license' ? 'bg-blue-100' : 'bg-purple-100'
                            }`}>
                              {doc.document_type === 'drivers_license' ? (
                                <Car className="w-6 h-6 text-blue-600" />
                              ) : (
                                <IdCard className="w-6 h-6 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold capitalize">{doc.document_type?.replace(/_/g, ' ')}</h3>
                              <p className="text-sm text-gray-500">Employee: {doc.user_name || doc.user_id}</p>
                              <p className="text-xs text-gray-400">
                                Uploaded: {new Date(doc.uploaded_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className={
                              doc.status === 'verified' ? 'bg-green-600' :
                              doc.status === 'rejected' ? 'bg-red-600' :
                              'bg-yellow-600'
                            }>
                              {doc.status === 'verified' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {doc.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                              {doc.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                              {doc.status}
                            </Badge>
                            
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle className="capitalize">{doc.document_type?.replace(/_/g, ' ')}</DialogTitle>
                                </DialogHeader>
                                {doc.file_type?.includes('pdf') ? (
                                  <iframe src={doc.file_url} className="w-full h-96 border rounded" title="Document Preview" />
                                ) : (
                                  <img src={doc.file_url} alt="Document" className="w-full rounded" />
                                )}
                                {doc.status === 'pending' && (
                                  <DialogFooter className="flex gap-2">
                                    <Button onClick={() => handleVerifyDocument(doc.document_id, 'verify')} className="bg-green-600">
                                      <Check className="w-4 h-4 mr-2" />
                                      Verify
                                    </Button>
                                    <Button variant="destructive" onClick={() => handleVerifyDocument(doc.document_id, 'reject', 'Document unclear or invalid')}>
                                      <X className="w-4 h-4 mr-2" />
                                      Reject
                                    </Button>
                                  </DialogFooter>
                                )}
                                {doc.rejection_reason && (
                                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                                    <p className="text-sm text-red-700"><strong>Rejection reason:</strong> {doc.rejection_reason}</p>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                        
                        {doc.status === 'pending' && (
                          <div className="flex gap-2 mt-4 pt-4 border-t">
                            <Button size="sm" onClick={() => handleVerifyDocument(doc.document_id, 'verify')} className="bg-green-600">
                              <Check className="w-4 h-4 mr-2" />
                              Verify
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleVerifyDocument(doc.document_id, 'reject', 'Document unclear')}>
                              <X className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
                <CardTitle>Service Management</CardTitle>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setEditingService(null);
                    setServiceForm({ service_type: '', name: '', price: '', category: 'roadside', description: '' });
                    setShowServiceForm(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {['roadside', 'mechanic', 'other'].map((category) => {
                    const categoryServices = services.filter(s => s.category === category);
                    if (categoryServices.length === 0) return null;
                    
                    return (
                      <div key={category}>
                        <h3 className="font-semibold text-gray-900 mb-3 capitalize flex items-center gap-2">
                          {category === 'roadside' && <Truck className="w-5 h-5 text-red-600" />}
                          {category === 'mechanic' && <Wrench className="w-5 h-5 text-blue-600" />}
                          {category === 'other' && <Shield className="w-5 h-5 text-gray-600" />}
                          {category} Services
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {categoryServices.map((service) => (
                            <div key={service.service_type} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium text-gray-900">{service.name}</h4>
                                <span className="text-lg font-bold text-blue-600">${service.price}</span>
                              </div>
                              <p className="text-sm text-gray-500 mb-3">{service.description}</p>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => editService(service)}>
                                  <Edit className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteService(service.service_type)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Service Form Dialog */}
            <Dialog open={showServiceForm} onOpenChange={setShowServiceForm}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleServiceSubmit} className="space-y-4">
                  <div>
                    <Label>Service ID (lowercase, no spaces)</Label>
                    <Input
                      value={serviceForm.service_type}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, service_type: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                      placeholder="oil_change"
                      disabled={!!editingService}
                      required
                    />
                  </div>
                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={serviceForm.name}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Oil Change"
                      required
                    />
                  </div>
                  <div>
                    <Label>Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={serviceForm.price}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="65.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={serviceForm.category} onValueChange={(v) => setServiceForm(prev => ({ ...prev, category: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="roadside">Roadside Assistance</SelectItem>
                        <SelectItem value="mechanic">Mobile Mechanic</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={serviceForm.description}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Service description..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                      {editingService ? 'Update Service' : 'Create Service'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowServiceForm(false)}>Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Full Account Management */}
              <AdminAccountManagement />

              {/* Owner Controls - Mass Actions */}
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Owner Account Controls
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    Powerful actions - use with caution. Only you (Owner) can see this section.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="destructive" className="bg-red-600 hover:bg-red-700">
                          <PowerOff className="w-4 h-4 mr-2" />
                          Deactivate All Accounts
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-red-600 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            Confirm Mass Deactivation
                          </DialogTitle>
                        </DialogHeader>
                        <p className="text-gray-600">
                          This will deactivate ALL registered accounts except yours. Users can still log in but will be marked as inactive.
                        </p>
                        <p className="text-sm text-gray-500">This action is reversible.</p>
                        <DialogFooter className="gap-2">
                          <Button variant="outline">Cancel</Button>
                          <Button 
                            variant="destructive"
                            onClick={async () => {
                              try {
                                const response = await axios.post(`${API}/owner/deactivate-all`, {}, {
                                  headers: getAuthHeaders(),
                                  withCredentials: true
                                });
                                toast.success(response.data.message);
                                fetchAdminData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to deactivate accounts');
                              }
                            }}
                          >
                            Deactivate All
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
                          <Power className="w-4 h-4 mr-2" />
                          Reactivate All Accounts
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-green-600">Confirm Mass Reactivation</DialogTitle>
                        </DialogHeader>
                        <p className="text-gray-600">
                          This will reactivate ALL deactivated accounts.
                        </p>
                        <DialogFooter className="gap-2">
                          <Button variant="outline">Cancel</Button>
                          <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              try {
                                const response = await axios.post(`${API}/owner/reactivate-all`, {}, {
                                  headers: getAuthHeaders(),
                                  withCredentials: true
                                });
                                toast.success(response.data.message);
                                fetchAdminData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to reactivate accounts');
                              }
                            }}
                          >
                            Reactivate All
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>

              {/* User List with Individual Actions */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>All Registered Users ({allUsers.length})</CardTitle>
                  <div className="flex gap-2 text-sm">
                    <Badge className="bg-green-100 text-green-700">
                      Active: {allUsers.filter(u => u.is_active !== false).length}
                    </Badge>
                    <Badge className="bg-red-100 text-red-700">
                      Inactive: {allUsers.filter(u => u.is_active === false).length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {allUsers.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No users found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-3 px-2">Name</th>
                            <th className="text-left py-3 px-2">Email</th>
                            <th className="text-left py-3 px-2">Type</th>
                            <th className="text-left py-3 px-2">Status</th>
                            <th className="text-left py-3 px-2">Admin</th>
                            <th className="text-left py-3 px-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allUsers.map((u) => (
                            <tr key={u.user_id} className={`border-b hover:bg-gray-50 ${u.is_active === false ? 'bg-red-50' : ''}`}>
                              <td className="py-2 px-2 font-medium">
                                {u.name}
                                {u.is_owner && <Badge className="ml-2 bg-purple-600 text-white text-xs">OWNER</Badge>}
                              </td>
                              <td className="py-2 px-2 text-gray-600 truncate max-w-[200px]">{u.email}</td>
                              <td className="py-2 px-2">
                                <Badge className={
                                  u.user_type === 'customer' ? 'bg-blue-100 text-blue-700' :
                                  u.user_type === 'mechanic' ? 'bg-green-100 text-green-700' :
                                  u.user_type === 'roadside_tech' ? 'bg-orange-100 text-orange-700' :
                                  u.user_type === 'admin' ? 'bg-indigo-100 text-indigo-700' :
                                  u.user_type === 'owner' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-700'
                                }>
                                  {u.user_type}
                                </Badge>
                              </td>
                              <td className="py-2 px-2">
                                <Badge className={u.is_active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                                  {u.is_active === false ? 'Inactive' : 'Active'}
                                </Badge>
                              </td>
                              <td className="py-2 px-2">
                                {u.user_id !== user.user_id && !u.is_owner ? (
                                  <Button
                                    size="sm"
                                    variant={u.is_admin ? "default" : "outline"}
                                    className={u.is_admin ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                                    onClick={() => handleToggleAdmin(u.user_id, !u.is_admin)}
                                  >
                                    {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                                  </Button>
                                ) : (
                                  <span className="text-gray-400 text-sm">{u.is_owner ? 'Owner' : 'You'}</span>
                                )}
                              </td>
                              <td className="py-2 px-2">
                                {u.user_id !== user.user_id && (
                                  <div className="flex gap-1">
                                    {/* Toggle Active Status */}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className={u.is_active === false ? 'text-green-600 hover:bg-green-50' : 'text-yellow-600 hover:bg-yellow-50'}
                                      onClick={async () => {
                                        try {
                                          await axios.put(`${API}/owner/users/${u.user_id}/toggle-status`, {}, {
                                            headers: getAuthHeaders(),
                                            withCredentials: true
                                          });
                                          toast.success(`User ${u.is_active === false ? 'activated' : 'deactivated'}`);
                                          fetchAdminData();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Failed to toggle status');
                                        }
                                      }}
                                      title={u.is_active === false ? 'Activate' : 'Deactivate'}
                                    >
                                      {u.is_active === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                                    </Button>

                                    {/* Delete User */}
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" title="Delete User">
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent>
                                        <DialogHeader>
                                          <DialogTitle className="text-red-600">Delete User Permanently?</DialogTitle>
                                        </DialogHeader>
                                        <p className="text-gray-600">
                                          Are you sure you want to permanently delete <strong>{u.name}</strong> ({u.email})?
                                        </p>
                                        <p className="text-sm text-red-500">This action cannot be undone.</p>
                                        <DialogFooter className="gap-2">
                                          <Button variant="outline">Cancel</Button>
                                          <Button 
                                            variant="destructive"
                                            onClick={async () => {
                                              try {
                                                await axios.delete(`${API}/owner/users/${u.user_id}`, {
                                                  headers: getAuthHeaders(),
                                                  withCredentials: true
                                                });
                                                toast.success(`User ${u.name} deleted`);
                                                fetchAdminData();
                                              } catch (error) {
                                                toast.error(error.response?.data?.detail || 'Failed to delete user');
                                              }
                                            }}
                                          >
                                            Delete Permanently
                                          </Button>
                                        </DialogFooter>
                                      </DialogContent>
                                    </Dialog>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]">
              {/* Conversations List */}
              <Card className="lg:col-span-1 overflow-hidden">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">All Conversations</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-y-auto h-[500px]">
                  {conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      onClick={() => {
                        setSelectedConversation(conv);
                        loadMessages(conv.conversation_id);
                      }}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                        selectedConversation?.conversation_id === conv.conversation_id ? 'bg-purple-50' : ''
                      } ${conv.is_paused ? 'bg-yellow-50' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm truncate">
                          {conv.participant_details?.map(p => p.name).join(', ')}
                        </span>
                        {conv.is_paused && <Pause className="w-4 h-4 text-yellow-600" />}
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(conv.last_message_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              {/* Messages */}
              <Card className="lg:col-span-2 flex flex-col">
                <CardHeader className="py-3 flex-row items-center justify-between">
                  <CardTitle className="text-sm">
                    {selectedConversation ? 
                      selectedConversation.participant_details?.map(p => p.name).join(', ') :
                      'Select a conversation'
                    }
                  </CardTitle>
                  {selectedConversation && (
                    <div className="flex gap-2">
                      {selectedConversation.is_paused ? (
                        <Button size="sm" variant="outline" onClick={() => resumeConversation(selectedConversation.conversation_id)}>
                          <Play className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => pauseConversation(selectedConversation.conversation_id)}>
                          <Pause className="w-4 h-4 mr-1" />
                          Pause
                        </Button>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender_type === 'system' ? 'bg-yellow-100 text-yellow-800 text-center w-full' :
                        msg.sender_id === user.user_id ? 'bg-purple-600 text-white' : 'bg-white border'
                      }`}>
                        {msg.sender_type !== 'system' && (
                          <p className={`text-xs mb-1 ${msg.sender_id === user.user_id ? 'text-purple-200' : 'text-gray-500'}`}>
                            {msg.sender_name}
                          </p>
                        )}
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
                {selectedConversation && (
                  <div className="p-3 border-t flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      disabled={selectedConversation.is_paused && !user.is_owner}
                    />
                    <Button onClick={sendMessage}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* AI Monitor Tab */}
          <TabsContent value="ai-monitor">
            <AIPerformanceMonitor initialSessionId={sessionIdFromUrl} />
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No activity logged yet</p>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className="p-4 bg-gray-50 rounded-lg flex items-start gap-4"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          notif.type === 'registration' ? 'bg-blue-100' :
                          notif.type === 'login' ? 'bg-green-100' :
                          notif.type === 'new_application' ? 'bg-yellow-100' :
                          notif.type === 'new_request' ? 'bg-purple-100' :
                          notif.type === 'sms_alert' ? 'bg-orange-100' :
                          'bg-gray-100'
                        }`}>
                          {notif.type === 'registration' && <Users className="w-5 h-5 text-blue-600" />}
                          {notif.type === 'login' && <Check className="w-5 h-5 text-green-600" />}
                          {notif.type === 'new_application' && <FileText className="w-5 h-5 text-yellow-600" />}
                          {notif.type === 'new_request' && <Car className="w-5 h-5 text-purple-600" />}
                          {notif.type === 'sms_alert' && <Phone className="w-5 h-5 text-orange-600" />}
                          {!['registration', 'login', 'new_application', 'new_request', 'sms_alert'].includes(notif.type) && 
                            <Bell className="w-5 h-5 text-gray-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{notif.subject}</p>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security / 2FA Tab */}
          <TabsContent value="security">
            <TwoFactorSettings />
          </TabsContent>

          {/* Provider Performance Analytics Tab */}
          <TabsContent value="performance">
            <ProviderPerformanceAnalytics />
          </TabsContent>

          {/* API Integrations Tab */}
          <TabsContent value="integrations">
            <IntegrationStatus />
          </TabsContent>

          {/* Revenue Hub Tab */}
          <TabsContent value="revenue">
            <OwnerRevenueHub />
          </TabsContent>

          {/* Payout Approvals Tab */}
          <TabsContent value="payouts">
            <PayoutApprovalDashboard isOwner={true} />
          </TabsContent>

          {/* Retention Bonuses Tab */}
          <TabsContent value="bonuses">
            <RetentionBonusDashboard isOwner={true} />
          </TabsContent>

          {/* Scraper Dashboard Tab */}
          <TabsContent value="scraper">
            <ScraperDashboard />
          </TabsContent>

          {/* Auto Parts Orders Tab */}
          <TabsContent value="auto-parts">
            <AutoPartsOrderApproval />
          </TabsContent>

          {/* Refund Logs Tab */}
          <TabsContent value="refunds">
            <RefundLogsDashboard />
          </TabsContent>

          {/* Data Backup Tab */}
          <TabsContent value="backups">
            <BackupDashboard />
          </TabsContent>

          {/* Leads Management Tab */}
          <TabsContent value="leads">
            <LeadsManagement getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Site Stats Override Tab */}
          <TabsContent value="site-stats">
            <SiteStatsEditor getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
